import { gql } from '@apollo/client';

// Household Mutations
export const CREATE_HOUSEHOLD = gql`
  mutation CreateHousehold($input: HouseholdInput!) {
    createHousehold(input: $input) {
      id
      name
      ownerId
      members
      settings {
        location
        weeklyBudget
        mealPrepTime
        dietaryPreferences
        allergies
        equipment
      }
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_HOUSEHOLD = gql`
  mutation UpdateHousehold($id: ID!, $input: HouseholdInput!) {
    updateHousehold(id: $id, input: $input) {
      id
      name
      settings {
        location
        weeklyBudget
        mealPrepTime
        dietaryPreferences
        allergies
        equipment
      }
      updatedAt
    }
  }
`;

export const DELETE_HOUSEHOLD = gql`
  mutation DeleteHousehold($id: ID!) {
    deleteHousehold(id: $id)
  }
`;

// Member Mutations
export const ADD_HOUSEHOLD_MEMBER = gql`
  mutation AddHouseholdMember($householdId: ID!, $email: String!) {
    addHouseholdMember(householdId: $householdId, email: $email) {
      householdId
      userId
      name
      email
      role
    }
  }
`;

export const UPDATE_MEMBER_PROFILE = gql`
  mutation UpdateMemberProfile($householdId: ID!, $userId: ID!, $input: MemberProfileInput!) {
    updateMemberProfile(householdId: $householdId, userId: $userId, input: $input) {
      householdId
      userId
      name
      email
      diet
      allergies
      dislikes
      preferences
      spiceLevel
      role
      updatedAt
    }
  }
`;

export const REMOVE_HOUSEHOLD_MEMBER = gql`
  mutation RemoveHouseholdMember($householdId: ID!, $userId: ID!) {
    removeHouseholdMember(householdId: $householdId, userId: $userId)
  }
`;

// Pantry Mutations
export const ADD_PANTRY_ITEM = gql`
  mutation AddPantryItem($householdId: ID!, $input: PantryItemInput!) {
    addPantryItem(householdId: $householdId, input: $input) {
      householdId
      itemId
      name
      category
      quantity
      unit
      expiryDate
      addedDate
      normalizedIngredientId
      perishabilityScore
      predictedExpirySource
      updatedAt
    }
  }
`;

export const UPDATE_PANTRY_ITEM = gql`
  mutation UpdatePantryItem($householdId: ID!, $itemId: ID!, $input: PantryItemInput!) {
    updatePantryItem(householdId: $householdId, itemId: $itemId, input: $input) {
      householdId
      itemId
      name
      category
      quantity
      unit
      expiryDate
      updatedAt
    }
  }
`;

export const DELETE_PANTRY_ITEM = gql`
  mutation DeletePantryItem($householdId: ID!, $itemId: ID!) {
    deletePantryItem(householdId: $householdId, itemId: $itemId)
  }
`;

export const BATCH_ADD_PANTRY_ITEMS = gql`
  mutation BatchAddPantryItems($householdId: ID!, $items: [PantryItemInput!]!) {
    batchAddPantryItems(householdId: $householdId, items: $items) {
      householdId
      itemId
      name
      category
      quantity
      unit
      expiryDate
      perishabilityScore
    }
  }
`;

// Meal Plan Mutations
export const GENERATE_MEAL_PLAN = gql`
  mutation GenerateMealPlan($householdId: ID!, $input: MealPlanGenerateInput!) {
    generateMealPlan(householdId: $householdId, input: $input) {
      householdId
      planId
      weekStart
      weekEnd
      planSlots {
        date
        mealType
        recipeId
        recipeName
        recipeImage
        recipeStub {
          id
          title
          image
          readyInMinutes
          servings
          nutrition {
            calories
            protein
            carbs
            fat
          }
        }
        status
        servings
      }
      nutritionSummary {
        dailyAverages {
          calories
          protein
          carbs
          fat
        }
      }
      status
      createdAt
    }
  }
`;

export const SWAP_RECIPE = gql`
  mutation SwapRecipe($householdId: ID!, $planId: ID!, $slotIndex: Int!, $newRecipeId: ID!) {
    swapRecipe(householdId: $householdId, planId: $planId, slotIndex: $slotIndex, newRecipeId: $newRecipeId) {
      householdId
      planId
      planSlots {
        date
        mealType
        recipeId
        recipeName
        recipeImage
        status
      }
      updatedAt
    }
  }
`;

export const SKIP_MEAL = gql`
  mutation SkipMeal($householdId: ID!, $planId: ID!, $slotIndex: Int!) {
    skipMeal(householdId: $householdId, planId: $planId, slotIndex: $slotIndex) {
      householdId
      planId
      planSlots {
        date
        mealType
        status
      }
      updatedAt
    }
  }
`;

export const REPLAN_MEALS = gql`
  mutation ReplanMeals($householdId: ID!, $planId: ID!) {
    replanMeals(householdId: $householdId, planId: $planId) {
      householdId
      planId
      planSlots {
        date
        mealType
        recipeId
        recipeName
        recipeImage
        status
      }
      nutritionSummary {
        dailyAverages {
          calories
          protein
          carbs
          fat
        }
      }
      updatedAt
    }
  }
`;

// Grocery List Mutations
export const GENERATE_GROCERY_LIST = gql`
  mutation GenerateGroceryList($householdId: ID!, $input: GroceryListInput!) {
    generateGroceryList(householdId: $householdId, input: $input) {
      householdId
      listId
      planId
      items {
        id
        name
        quantity
        unit
        category
        aisle
        checked
        recipeIds
        notes
      }
      instacartLinkUrl
      status
      createdAt
    }
  }
`;

export const UPDATE_GROCERY_ITEM = gql`
  mutation UpdateGroceryItem($householdId: ID!, $listId: ID!, $itemId: ID!, $checked: Boolean!) {
    updateGroceryItem(householdId: $householdId, listId: $listId, itemId: $itemId, checked: $checked) {
      householdId
      listId
      items {
        id
        name
        checked
      }
      updatedAt
    }
  }
`;

export const ADD_GROCERY_ITEM = gql`
  mutation AddGroceryItem($householdId: ID!, $listId: ID!, $input: GroceryItemInput!) {
    addGroceryItem(householdId: $householdId, listId: $listId, input: $input) {
      householdId
      listId
      items {
        id
        name
        quantity
        unit
        category
        aisle
        checked
      }
      updatedAt
    }
  }
`;

export const REMOVE_GROCERY_ITEM = gql`
  mutation RemoveGroceryItem($householdId: ID!, $listId: ID!, $itemId: ID!) {
    removeGroceryItem(householdId: $householdId, listId: $listId, itemId: $itemId) {
      householdId
      listId
      items {
        id
        name
      }
      updatedAt
    }
  }
`;

// Feedback Mutations
export const SUBMIT_FEEDBACK = gql`
  mutation SubmitFeedback($recipeId: ID!, $rating: Int!, $reason: String, $tags: [String!]) {
    submitFeedback(recipeId: $recipeId, rating: $rating, reason: $reason, tags: $tags) {
      userId
      recipeId
      rating
      reason
      tags
      createdAt
    }
  }
`;

// Trip Mutations
export const CREATE_TRIP = gql`
  mutation CreateTrip($householdId: ID!, $input: TripInput!) {
    createTrip(householdId: $householdId, input: $input) {
      householdId
      tripId
      name
      type
      startDate
      endDate
      groupSize
      location
      gear
      planId
      createdAt
    }
  }
`;

export const UPDATE_TRIP = gql`
  mutation UpdateTrip($householdId: ID!, $tripId: ID!, $input: TripInput!) {
    updateTrip(householdId: $householdId, tripId: $tripId, input: $input) {
      householdId
      tripId
      name
      type
      startDate
      endDate
      groupSize
      location
      gear
      updatedAt
    }
  }
`;

export const DELETE_TRIP = gql`
  mutation DeleteTrip($householdId: ID!, $tripId: ID!) {
    deleteTrip(householdId: $householdId, tripId: $tripId)
  }
`;

export const GENERATE_TRIP_MEAL_PLAN = gql`
  mutation GenerateTripMealPlan($householdId: ID!, $tripId: ID!) {
    generateTripMealPlan(householdId: $householdId, tripId: $tripId) {
      householdId
      planId
      weekStart
      weekEnd
      planSlots {
        date
        mealType
        recipeName
        recipeImage
        status
      }
      status
      createdAt
    }
  }
`;
